import { useFirestore } from 'reactfire'
import { useParams } from 'react-router-dom'
import { useState } from 'react'
import {
  Button,
  Col,
  ConfigProvider,
  Form,
  Input,
  Row,
  Space,
  theme,
  Typography,
} from 'antd'
import { doc, setDoc } from 'firebase/firestore'
import { errorResponse } from '../../lib/errorResponse'
import { gutter } from '../../lib/layout'
import { accountingSoftware } from './accountingSoftware'
import cloudDataTransfer from '../../resources/img/cloud-data-transfer.svg'
import { useTranslation } from 'react-i18next'

export function StepSoftware({ onboarding, goToNext, goToPrevious }) {
  const firestore = useFirestore()
  const { companyUid } = useParams()
  const [loading, setLoading] = useState(false)

  const { token } = theme.useToken()
  const { t } = useTranslation()

  async function onSubmit(values) {
    setLoading(true)
    try {
      const finalValues = {
        software: values.software,
        acceptedDeregistration:
          onboarding.software === values.software
            ? onboarding.acceptedDeregistration
            : null, // Reset the checkbox if the software is changed
        miscSoftwareName: values.miscSoftwareName?.trim() || null,
      }

      await setDoc(doc(firestore, 'onboarding', companyUid), finalValues, {
        merge: true,
      })
      goToNext()
    } catch (e) {
      errorResponse(e)
    } finally {
      setLoading(false)
    }
  }

  const [form] = Form.useForm()

  const software = Form.useWatch(['software'], { form, preserve: true })

  const initialValues = {
    software: onboarding.software,
    miscSoftwareName: onboarding.miscSoftwareName,
  }

  return (
    <>
      <img
        style={{
          position: 'absolute',
          top: 0,
          left: -320,
          width: 700,
          zIndex: 1,
        }}
        src={cloudDataTransfer}
        alt=""
      />
      <Typography.Paragraph>
        {t('frontend:onboarding.softwareIntro')}
      </Typography.Paragraph>

      <div
        style={{
          position: 'relative',
          zIndex: 2,
          maxWidth: '35em',
          marginLeft: 'auto',
        }}
      >
        <Form
          style={{ marginTop: token.marginXL }}
          form={form}
          onFinish={onSubmit}
          colon={false}
          layout="vertical"
          initialValues={initialValues}
        >
          <Row gutter={gutter}>
            <Col span={24}>
              <Form.Item
                name={['software']}
                rules={[
                  {
                    required: true,
                    message: t('frontend:onboarding.fieldRequired'),
                  },
                ]}
              >
                <SoftwareSelect
                  deregistrationReason={onboarding.deregistrationReason}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              {software === 'misc' && (
                <Form.Item
                  label={t('frontend:onboarding.softwareNameLabel')}
                  name={['miscSoftwareName']}
                  help={t('frontend:onboarding.softwareNameHelp')}
                  rules={[
                    {
                      required: true,
                      message: t('frontend:onboarding.fieldRequired'),
                      whitespace: true,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              )}
            </Col>
          </Row>

          <div style={{ textAlign: 'right', marginTop: token.marginLG }}>
            <Space>
              <Button type="text" onClick={() => goToPrevious()}>
                {t('frontend:common.back')}
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {t('frontend:onboarding.next')}
              </Button>
            </Space>
          </div>
        </Form>
      </div>
    </>
  )
}

function SoftwareSelect({ value, onChange, deregistrationReason, ...props }) {
  const { token } = theme.useToken()
  const { t } = useTranslation()

  return (
    <ConfigProvider
      theme={{
        components: {
          Button: {
            controlHeight: token.controlHeightLG * 2,
          },
        },
      }}
    >
      <Row gutter={gutter}>
        {Object.entries(accountingSoftware).map(([key, { label }]) => {
          const displayLabel =
            key === 'misc' ? t('frontend:onboarding.softwareMisc') : label
          return (
            <Col xs={24} sm={12} key={key}>
              <Button
                danger={props['aria-invalid']}
                type={value === key ? 'primary' : 'default'}
                block
                onClick={() => onChange(key)}
              >
                {key === 'agenda' && deregistrationReason === '36' ? (
                  <>{displayLabel}</>
                ) : (
                  displayLabel
                )}
              </Button>
            </Col>
          )
        })}
      </Row>
    </ConfigProvider>
  )
}
